import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Card, Space, Button, Spin, Tree, Typography, message, Alert, Tooltip,
} from 'antd';
import {
  EyeOutlined, EyeInvisibleOutlined, SaveOutlined, UndoOutlined,
  FolderOutlined, FileOutlined, MenuOutlined, InfoCircleOutlined,
  ReloadOutlined, PushpinOutlined, PushpinFilled,
} from '@ant-design/icons';
import { isAdmin } from 'utils/permission';
import { checkToken } from 'api/utils';
import {
  getSidebarConfig,
  putSidebarConfig,
} from 'api/configuraciones/sidebarConfig';
import {
  getMenuOrder,
  postMenuOrder,
} from 'api/configuraciones/menuOrder';
import {
  applyVisualOrder,
  treeToMenuStructure,
} from 'utils/sidebarMenuConfig';

const { Title } = Typography;

export const permisosSidebarPermission = {
  permissionModel: 'sidebarconfig',
  subModels: ['menuorder'],
  catalogsModels: [],
};

// No se puede ocultar el módulo Configuración ni esta misma página, o un usuario
// podría quedarse sin forma de revertir la configuración desde la UI.
const RUTAS_PROHIBIDAS = ['/configuracion', '/configuracion/accesos/permisos-sidebar'];

// routesTree llega por prop (inyectado por App.js, igual que `permission`) en vez de
// importarse aquí desde routes/index: routes/index importa routes/configuracion, que
// importa esta vista — un import directo aquí crearía un ciclo de dependencias.
const PermisosSidebar = ({ routesTree }) => {
  const puedeEditar = isAdmin();
  const [loading, setLoading] = useState(false);
  const [treeData, setTreeData] = useState([]);
  const [hiddenPaths, setHiddenPaths] = useState([]);

  const cargar = useCallback(async () => {
    setLoading(true);
    await checkToken();
    const [sidebarConfig, menuOrder] = await Promise.all([
      getSidebarConfig(),
      getMenuOrder(),
    ]);
    const arbolVisual = applyVisualOrder(routesTree, menuOrder?.menu_structure);
    setTreeData(arbolVisual);
    setHiddenPaths(sidebarConfig?.hidden_modules || []);
    setLoading(false);
  }, [routesTree]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (mounted) await cargar();
    })();
    return () => { mounted = false; };
  }, [cargar]);

  const toggleHidden = (fullPath) => {
    if (!puedeEditar) return;
    if (RUTAS_PROHIBIDAS.includes(fullPath)) {
      message.error('No puedes ocultar el módulo Configuración ni esta página.');
      return;
    }
    setHiddenPaths((prev) => (prev.includes(fullPath)
      ? prev.filter((p) => p !== fullPath)
      : [...prev, fullPath]));
  };

  const toggleOpenByDefault = (fullPath) => {
    if (!puedeEditar) return;
    const marcar = (nodes) => nodes.map((node) => {
      if (node.fullPath === fullPath) return { ...node, openByDefault: !node.openByDefault };
      if (node.children?.length) return { ...node, children: marcar(node.children) };
      return node;
    });
    setTreeData((prev) => marcar(prev));
  };

  // Algoritmo estándar de reordenamiento de <Tree> de antd: ubica el nodo arrastrado por
  // su posición (pos), lo quita de donde estaba e lo inserta en el destino (antes/después/
  // dentro, según dropToGap). El fullPath de cada nodo nunca cambia al moverlo.
  const onDrop = (info) => {
    if (!puedeEditar) return;
    const dropKey = info.node.key;
    const dragKey = info.dragNode.key;
    const dropPos = info.node.pos.split('-');
    const dropPosition = info.dropPosition - Number(dropPos[dropPos.length - 1]);

    // Ojo: treeData (el estado crudo) identifica cada nodo por `fullPath`, no por `key`
    // — `key` solo existe en el árbol renderizado que arma buildTreeProps(). dragKey/dropKey
    // vienen de ese árbol renderizado, así que hay que comparar contra fullPath aquí.
    const loop = (data, fullPath, callback) => {
      data.forEach((item, index) => {
        if (item.fullPath === fullPath) {
          callback(item, index, data);
          return;
        }
        if (item.children) loop(item.children, fullPath, callback);
      });
    };

    const data = JSON.parse(JSON.stringify(treeData));
    let dragObj;
    loop(data, dragKey, (item, index, arr) => {
      arr.splice(index, 1);
      dragObj = item;
    });

    if (!info.dropToGap) {
      loop(data, dropKey, (item) => {
        const nodo = item;
        nodo.children = nodo.children || [];
        nodo.children.unshift(dragObj);
      });
    } else {
      let ar = [];
      let i = 0;
      loop(data, dropKey, (_item, index, arr) => {
        ar = arr;
        i = index;
      });
      if (dropPosition === -1) {
        ar.splice(i, 0, dragObj);
      } else {
        ar.splice(i + 1, 0, dragObj);
      }
    }
    setTreeData(data);
  };

  const onSave = async () => {
    setLoading(true);
    const [okHidden, okOrden] = await Promise.all([
      putSidebarConfig(hiddenPaths),
      postMenuOrder(treeToMenuStructure(treeData)),
    ]);
    if (okHidden && okOrden) {
      message.success('Configuración de sidebar guardada. Los usuarios la verán en su próximo inicio de sesión.');
    }
    setLoading(false);
  };

  const onReset = async () => {
    setLoading(true);
    const [okHidden, okOrden] = await Promise.all([
      putSidebarConfig([]),
      postMenuOrder([]),
    ]);
    if (okHidden && okOrden) {
      setTreeData(routesTree);
      setHiddenPaths([]);
      message.success('Configuración de sidebar restablecida al orden y visibilidad por defecto.');
    } else {
      setLoading(false);
    }
  };

  const buildTreeProps = (nodes) => nodes.map((node) => {
    const oculto = hiddenPaths.includes(node.fullPath);
    const esProhibido = RUTAS_PROHIBIDAS.includes(node.fullPath);
    const tieneHijos = !!node.children?.length;
    return {
      key: node.fullPath,
      selectable: false,
      draggable: puedeEditar,
      children: tieneHijos ? buildTreeProps(node.children) : undefined,
      title: (
        <Space style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <Space>
            {tieneHijos ? (
              <FolderOutlined style={{ color: '#faad14' }} />
            ) : (
              <FileOutlined style={{ color: '#1890ff' }} />
            )}
            <span style={{
              textDecoration: oculto ? 'line-through' : 'none',
              color: oculto ? '#999' : 'inherit',
              fontWeight: tieneHijos ? 500 : 'normal',
            }}
            >
              {node.sidebarTitle || node.title}
            </span>
            <small style={{ color: '#bbb' }}>{node.fullPath}</small>
          </Space>
          {puedeEditar && (
            <Space size="small" onClick={(e) => e.stopPropagation()}>
              {tieneHijos && (
                <Tooltip title={node.openByDefault ? 'Se muestra expandido al entrar (clic para desactivar)' : 'Activar para que se muestre expandido al entrar'}>
                  <Button
                    type="text"
                    size="small"
                    icon={node.openByDefault ? <PushpinFilled style={{ color: '#1890ff' }} /> : <PushpinOutlined />}
                    onClick={() => toggleOpenByDefault(node.fullPath)}
                  />
                </Tooltip>
              )}
              {!esProhibido && (
                <Tooltip title={oculto ? 'Mostrar en el sidebar' : 'Ocultar del sidebar'}>
                  <Button
                    type="text"
                    size="small"
                    icon={oculto ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                    onClick={() => toggleHidden(node.fullPath)}
                  />
                </Tooltip>
              )}
            </Space>
          )}
        </Space>
      ),
    };
  });

  return (
    <Spin spinning={loading}>
      <Card
        title={(
          <Space>
            <MenuOutlined />
            <Title level={4} style={{ margin: 0 }}>Permisos de Sidebar</Title>
          </Space>
        )}
        extra={(
          <Space>
            <Button icon={<ReloadOutlined />} onClick={cargar} disabled={loading}>
              Recargar del Servidor
            </Button>
            {puedeEditar && (
              <>
                <Button icon={<UndoOutlined />} onClick={onReset} disabled={loading}>
                  Restablecer Todo
                </Button>
                <Button type="primary" icon={<SaveOutlined />} onClick={onSave} loading={loading}>
                  Guardar Cambios
                </Button>
              </>
            )}
          </Space>
        )}
      >
        {!puedeEditar && (
          <Alert
            type="warning"
            showIcon
            message="Solo lectura"
            description="Solo un administrador de la entidad puede modificar la configuración del menú. Puedes ver cómo está configurado, pero los cambios no se pueden guardar."
            style={{ marginBottom: 16 }}
          />
        )}
        <Alert
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
          message="Configuración del Menú"
          description={(
            <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
              <li>
                <strong>Arrastra y suelta</strong>
                {' '}
                para reorganizar el orden del menú.
              </li>
              <li>
                Usa el ojo (
                <EyeOutlined />
                ) para ocultar o mostrar módulos.
              </li>
              <li>
                Usa el pin (
                <PushpinOutlined />
                ) — solo en módulos con submenú — para que aparezca expandido al entrar,
                en vez de colapsado.
              </li>
            </ul>
          )}
          style={{ marginBottom: 16 }}
        />
        <Tree
          treeData={buildTreeProps(treeData)}
          draggable={puedeEditar}
          onDrop={puedeEditar ? onDrop : undefined}
          blockNode
          showLine
          defaultExpandAll
          style={{
            backgroundColor: '#fff',
            padding: 16,
            borderRadius: 8,
            border: '1px solid #f0f0f0',
            minHeight: 400,
            maxHeight: 600,
            overflow: 'auto',
          }}
        />
      </Card>
    </Spin>
  );
};

PermisosSidebar.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  routesTree: PropTypes.arrayOf(PropTypes.object),
};

PermisosSidebar.defaultProps = {
  routesTree: [],
};

export default PermisosSidebar;
